import {Injectable, OnDestroy} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';

import {
  AppState,
  selectConfiguration,
  selectRouter,
  successCoreSettings,
  track
} from '../../core.state';
import {Store} from "@ngrx/store";

import {
  failManager,
  getLeaderboard,
  getTopFollowed,
  loadLeaderboardWidget,
  loadMoreLeaderboard,
  loadTopPositiveROI,
  reloadLeaderboard,
  successLeaderboard, successTopFollowed, successTopPositiveROI
} from "../manager.actions";

import {ManagerClient} from "../../../client/manager";
import {routerNavigatedAction} from "@ngrx/router-store";
import {catchError, exhaustMap, filter, map, takeUntil, withLatestFrom} from "rxjs/operators";
import {EMPTY, of, Subject, switchMap} from "rxjs";
import {selectManagersState} from "../manager.selectors";
import {LeaderboardCacheService} from "./leaderboard-widget.cache.service";

@Injectable()

export class LeaderboardRelatedEffects implements OnDestroy {
  private readonly destroy$ = new Subject<void>();

  routerNavigationLeaderboard = createEffect(() => {
    return this.actions$.pipe(
      ofType(routerNavigatedAction, successCoreSettings),
      withLatestFrom(this.store.select(selectRouter), this.store.select(selectConfiguration)),
      filter(([action, router, config]) => {
        if (!router?.state?.url || !config?.coreSettings) {
          return false;
        }
        return router.state.url.includes('leaderboard');
      }),
      takeUntil(this.destroy$),
      exhaustMap(([action, router, config]) => {
        const sport = router.state.params.sport ? router.state.params.sport.toUpperCase() : 'ALL';
        // Upstream filter guarantees config.coreSettings is defined
        const sport_category = config.coreSettings!.sport_categories.find(
          (cat: any) => cat.code === sport
        );
        return of(getLeaderboard({
          model: {
            sport_category: sport_category,
            page: 1
          }
        }));
      }))
  }, {dispatch: true});

  reloadLeaderboard = createEffect(() => {
    return this.actions$.pipe(
      ofType(reloadLeaderboard),
      withLatestFrom(this.store.select(selectManagersState)),
      takeUntil(this.destroy$),
      exhaustMap(([propVal, managersState]) => {
        return of(getLeaderboard({
          model: Object.assign({},
            managersState.leaderboardParams,
            {
              page: 1
            }
          )
        }))
      })
    )
  });

  loadMoreLeaderboard = createEffect(() => {
    return this.actions$.pipe(
      ofType(loadMoreLeaderboard),
      withLatestFrom(this.store.select(selectManagersState)),
      takeUntil(this.destroy$),
      exhaustMap(([propVal, managersState]) => {
        // Pre-existing assumption: leaderboardParams is populated whenever this action fires — not independently verified
        return of(getLeaderboard({
          model: Object.assign({},
            managersState.leaderboardParams,
            {
              page: managersState.leaderboardParams!.page + 1
            }
          )
        }))
      })
    )
  });

  // getLeaderboard = createEffect(() => {
  //   return this.actions$.pipe(
  //     ofType(getLeaderboard),
  //     withLatestFrom(this.store.select(selectManagersState)),
  //     takeUntil(this.destroy$),
  //     exhaustMap(([propVal, managersState]) => {
  //
  //       return this.managerClient.get_leaderboard(
  //         propVal.model.isWidget ? managersState.leaderboardWidgetParams :
  //           managersState.leaderboardParams
  //       ).pipe(
  //         map(data => {
  //           return successLeaderboard({response: data});
  //         }),
  //         catchError(error => {
  //           return of(failManager({error: error}))
  //         })
  //       )
  //     })
  //   )
  // });

  /** Use 24h caching for this shite **/
  getLeaderboard = createEffect(() => {
    return this.actions$.pipe(
      ofType(getLeaderboard),
      withLatestFrom(this.store.select(selectManagersState)),
      takeUntil(this.destroy$),
      switchMap(([propVal, managersState]) => {
        const params = propVal.model.isWidget ?
          managersState.leaderboardWidgetParams :
          managersState.leaderboardParams;

        // Create a cache key from params
        const cacheKey = JSON.stringify(params);

        // Check if we have valid cached data
        const cachedResponse = this.cacheService.get(cacheKey);
        if (cachedResponse) {
          return of(successLeaderboard({ response: cachedResponse }));
        }

        // If no cache, make the API call
        return this.managerClient.get_leaderboard(params).pipe(
          map(data => {
            // Cache the response with the params-based key
            this.cacheService.set(cacheKey, data);
            return successLeaderboard({ response: data });
          }),
          catchError(error => of(failManager({ error })))
        );
      })
    );
  });




  trackGetLeaderboard = createEffect(() => {
    return this.actions$.pipe(
      ofType(getLeaderboard),
      withLatestFrom(this.store.select(selectManagersState)),
      filter(([propVal, managersState]) => !propVal.model.isWidget),
      takeUntil(this.destroy$),
      exhaustMap(([propVal, managersState]) => {
        // Pre-existing assumption: leaderboardParams is populated whenever a getLeaderboard action fires — not independently verified
        const params = managersState.leaderboardParams!;

        const eventData = {
          type: params.sport_category!.code,
          period: `${params.period!.time_value}_${params.period!.time_key}`,
          open_syndicates: params.open_syndicates
        }

        return of(track({event: eventData}))
      })
    )
  });

  loadLeaderboardWidget = createEffect(() => {
    return this.actions$.pipe(
      ofType(loadLeaderboardWidget, successCoreSettings),
      withLatestFrom(this.store.select(selectConfiguration), this.store.select(selectManagersState)),
      takeUntil(this.destroy$),
      exhaustMap((propVal) => {
        const coreSettings = propVal[1].coreSettings;
        const leaderboardWidgetParams = propVal[2].leaderboardWidgetParams;
        if (!coreSettings || !leaderboardWidgetParams?.sportId) return EMPTY;
        const sport_category = coreSettings.sport_categories.find(
          (cat: any) => cat.id === leaderboardWidgetParams.sportId
        );
        return of(getLeaderboard({
          model: {
            sport_category: sport_category,
            page: 1,
            per: 4,
            isWidget: true
          }
        }));
      }))
  }, {dispatch: true});

  loadTopROICaptains = createEffect(() => {
    return this.actions$.pipe(
      ofType(loadTopPositiveROI, successCoreSettings),
      withLatestFrom(this.store.select(selectConfiguration), this.store.select(selectManagersState)),
      takeUntil(this.destroy$),
      exhaustMap((propVal) => {
        const coreSettings = propVal[1].coreSettings;
        const leaderboardWidgetParams = propVal[2].leaderboardWidgetParams;
        if (!coreSettings || !leaderboardWidgetParams?.sportId) return EMPTY;
        const sport_category = coreSettings.sport_categories.find(
          (cat: any) => cat.id === leaderboardWidgetParams.sportId
        );
        const model = {
          sport_category: sport_category,
          page: 1,
          per: leaderboardWidgetParams.per,
          period: leaderboardWidgetParams.period,
          sort_by: leaderboardWidgetParams.sort_by,
          open_syndicates: leaderboardWidgetParams.open_syndicates,
          isWidget: true
        };

        return this.managerClient.get_leaderboard(model).pipe(
          map(data => {
            return successTopPositiveROI({response: data});
          }),
          catchError(error => {
            return of(failManager({error: error}))
          })
        )
      }))
  }, {dispatch: true});

  getTopFollowed = createEffect(() => {
    return this.actions$.pipe(
      ofType(getTopFollowed),
      takeUntil(this.destroy$),
      exhaustMap((model: any) => {
        return this.managerClient.get_leaderboard(model).pipe(
          map(data => {
            return successTopFollowed({response: data});
          }),
          catchError(error => {
            return of(failManager({error: error}))
          })
        )
      })
    )
  });

  constructor(
    public actions$: Actions,
    public store: Store<AppState>,
    public managerClient: ManagerClient,
    private cacheService: LeaderboardCacheService
  ) {
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
